import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://protegeyagt.com'}/api`;

const RecurrenteIntegrationCard = ({ settings, updateSettings, plans }) => {
  const [localMappings, setLocalMappings] = useState(settings.recurrente?.plan_mappings || {});
  const [saving, setSaving] = useState(false);
  const [customMappings, setCustomMappings] = useState([]);

  const handleMappingChange = (planId, value) => {
    setLocalMappings(prev => ({
      ...prev,
      [planId]: value
    }));
  };

  const handleRemoveMapping = (planId) => {
    const newMappings = { ...localMappings };
    delete newMappings[planId];
    setLocalMappings(newMappings);
  };

  const handleAddCustomMapping = () => {
    const newId = `custom_${Date.now()}`;
    setCustomMappings(prev => [...prev, { id: newId, name: '', recurrente_id: '' }]);
  };

  const handleRemoveCustomMapping = (customId) => {
    setCustomMappings(prev => prev.filter(item => item.id !== customId));
  };

  const handleSaveMappings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      // Prepare mappings including custom ones
      const finalMappings = { ...localMappings };
      customMappings.forEach(custom => {
        if (custom.name && custom.recurrente_id) {
          finalMappings[custom.id] = custom.recurrente_id;
        }
      });

      const newSettings = {
        ...settings,
        recurrente: {
          ...settings.recurrente,
          plan_mappings: finalMappings
        }
      };

      await axios.put(`${API}/admin/settings`, newSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateSettings(newSettings);
      toast.success('Mapeo de planes guardado exitosamente');
    } catch (error) {
      console.error('Error saving mappings:', error);
      toast.error('Error guardando el mapeo de planes');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (planId, recurrenteId) => {
    if (!recurrenteId) {
      toast.error('Ingrese un ID de producto válido');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      
      toast.info('Probando conexión con Recurrente...');
      
      const response = await axios.post(
        `${API}/admin/test-recurrente-product`, 
        { product_id: recurrenteId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        const product = response.data.product;
        
        if (product && product.name) {
          // Respuesta completa con información del producto
          toast.success(
            <div>
              <strong>✅ Conexión exitosa</strong><br/>
              <small>Producto: {product.name}</small><br/>
              <small>Precio: {product.price || 'No definido'}</small>
            </div>,
            { duration: 4000 }
          );
        } else {
          // Respuesta exitosa pero sin detalles del producto (ej: ping endpoint)
          toast.success(
            <div>
              <strong>✅ Conexión exitosa con Recurrente</strong><br/>
              <small>{response.data.message || 'Servidor accesible y autenticación correcta'}</small><br/>
              {response.data.details && <small>{response.data.details}</small>}
            </div>,
            { duration: 4000 }
          );
        }
      } else {
        toast.error(`❌ Error: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error testing connection:', error);
      if (error.response?.status === 404) {
        toast.error('❌ Producto no encontrado en Recurrente');
      } else if (error.response?.status === 401) {
        toast.error('❌ API Key inválida o expirada');
      } else {
        toast.error(`❌ Error de conexión: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Integración Recurrente</CardTitle>
        <CardDescription>Configuración para pagos recurrentes con mapeo de planes</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>API Key</Label>
            <Input 
              type="password"
              placeholder="pk_test_..."
              value={settings.recurrente?.recurrente_api_key || ''}
              onChange={(e) => {
                const newSettings = {
                  ...settings,
                  recurrente: { ...settings.recurrente, recurrente_api_key: e.target.value }
                };
                updateSettings(newSettings);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>Webhook Secret</Label>
            <Input 
              type="password"
              placeholder="whsec_..."
              value={settings.recurrente?.recurrente_webhook_secret || ''}
              onChange={(e) => {
                const newSettings = {
                  ...settings,
                  recurrente: { ...settings.recurrente, recurrente_webhook_secret: e.target.value }
                };
                updateSettings(newSettings);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>URL Base API</Label>
            <Input 
              placeholder="https://api.recurrente.com"
              value={settings.recurrente?.base_url || ''}
              onChange={(e) => {
                const newSettings = {
                  ...settings,
                  recurrente: { ...settings.recurrente, base_url: e.target.value }
                };
                updateSettings(newSettings);
              }}
            />
          </div>

          {/* Plan Mapping Section */}
          <div className="space-y-4 border-t pt-4">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-sm">Mapeo de Planes</h4>
                <p className="text-xs text-gray-500">
                  Asocia cada plan local con un producto de Recurrente
                </p>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleAddCustomMapping}
              >
                + Agregar Mapeo
              </Button>
            </div>
            
            {plans && plans.length > 0 ? (
              <div className="space-y-3">
                {plans.map((plan) => (
                  <div key={plan.id} className="flex items-center space-x-3 p-3 border rounded">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{plan.name}</p>
                      <p className="text-xs text-gray-500">Q{plan.price_q_monthly}/mes</p>
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">ID Producto Recurrente</Label>
                      <Input 
                        placeholder="prod_xxxxx"
                        className="mt-1"
                        value={localMappings[plan.id] || ''}
                        onChange={(e) => handleMappingChange(plan.id, e.target.value)}
                      />
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => testConnection(plan.id, localMappings[plan.id])}
                        disabled={!localMappings[plan.id]}
                      >
                        Probar
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRemoveMapping(plan.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}

                {/* Custom Mappings */}
                {customMappings.map((custom) => (
                  <div key={custom.id} className="flex items-center space-x-3 p-3 border rounded bg-blue-50">
                    <div className="flex-1">
                      <Label className="text-xs">Nombre del Plan</Label>
                      <Input 
                        placeholder="Ej: Plan Premium"
                        className="mt-1"
                        value={custom.name}
                        onChange={(e) => {
                          setCustomMappings(prev => prev.map(item => 
                            item.id === custom.id ? { ...item, name: e.target.value } : item
                          ));
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">ID Producto Recurrente</Label>
                      <Input 
                        placeholder="prod_xxxxx"
                        className="mt-1"
                        value={custom.recurrente_id}
                        onChange={(e) => {
                          setCustomMappings(prev => prev.map(item => 
                            item.id === custom.id ? { ...item, recurrente_id: e.target.value } : item
                          ));
                        }}
                      />
                    </div>
                    <div className="flex space-x-2 pt-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleRemoveCustomMapping(custom.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded text-center">
                <p className="text-sm text-gray-600">No hay planes configurados</p>
              </div>
            )}

            {/* Save Button */}
            <div className="flex justify-end space-x-2 pt-4 border-t">
              <Button 
                onClick={handleSaveMappings}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                {saving ? 'Guardando...' : 'Guardar Cambios'}
              </Button>
            </div>
          </div>

          <div className="p-3 bg-green-50 border-l-4 border-green-400 rounded">
            <p className="text-sm text-green-700">
              ✅ <strong>Integración Funcional:</strong> Los pagos se procesarán automáticamente cuando configures las credenciales.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default RecurrenteIntegrationCard;